'use client'

import Button, { buttonClassNames } from '@components/primitives/Button'
import { default as Modal } from '@components/primitives/Modal/index.client'
import { classMerge } from '@components/utilities/classMerge'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

export default function ExitIntentModal() {
  const router = useRouter()
  const [showExitModal, setShowExitModal] = useState(false)
  const [hasShownModal, setHasShownModal] = useState(false)

  const handleContinue = useCallback(() => {
    trackEvent(TrackingEvents.EXIT_INTENT_MODAL_CONTINUE_CLICKED)
    setShowExitModal(false)
    setHasShownModal(true) // Mark as shown so we don't show it again
  }, [])

  useEffect(() => {
    // Don't set up listener if we've already shown the modal to the user before
    if (hasShownModal) {
      return
    }

    const handlePopState = () => {
      if (showExitModal || hasShownModal) {
        return
      }

      // Track modal opening and show modal
      trackEvent(TrackingEvents.EXIT_INTENT_MODAL_OPENED)
      setShowExitModal(true)
    }

    // Push initial hash to prevent back navigation
    router.push('#preventBack', { scroll: false })

    // Listen for back navigation
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  return (
    <Modal
      preventClose
      showHeader={false}
      size='Large'
      visible={showExitModal}
      wrapperClassName='bg-neutral400/75'
    >
      <div className='flex flex-col gap-6 p-6 pb-0'>
        {/* Header */}
        <h2 className={classMerge('text-2xl font-bold text-neutral900')}>
          {`Don't miss your chance to join!`}
        </h2>

        {/* Scarcity Message */}
        <div>
          <p
            className={classMerge(
              'inline text-base font-semibold text-neutral900',
            )}
          >
            53 patients are interested in our trials today.
          </p>
          <p
            className={classMerge(
              'inline text-base font-medium text-neutral900',
            )}
          >
            {' '}
            Spots are limited — complete your application to secure yours today!
          </p>
        </div>

        {/* Social Proof Section */}
        <div className='rounded-lg bg-primary50 p-6'>
          <p className={classMerge('mb-2 text-sm tracking-widest')}>
            CUT THE LINE FOR INNOVATION
          </p>
          <p className={classMerge('text-sm font-medium text-neutral700')}>
            {`By joining a trial, you can access treatments not yet available anywhere else. Complete your application to see if you're eligible!`}
          </p>
        </div>

        {/* Action Button */}
        <div className='flex justify-center'>
          <Button
            className={classMerge(
              buttonClassNames.primary.black,
              'w-full rounded-full',
            )}
            onClick={handleContinue}
            value='Continue'
          />
        </div>
      </div>
    </Modal>
  )
}
